import React, { useState } from 'react';
import { toast } from 'sonner';
import { Database, X } from 'lucide-react';
import { useApp } from '../context/AppContext';

// Shown once if a logged-in user has data in legacy localStorage. Lets them
// import it into MongoDB or dismiss permanently.
export default function MigrationPrompt() {
  const { getLegacyState, importLegacyState, dismissLegacyState } = useApp();
  const [legacy] = useState(() => getLegacyState());
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);

  if (!legacy || hidden) return null;

  const sourceCount = (legacy.sources || []).length;
  const snapshotCount = (legacy.snapshots || []).length;
  const assetCount = (legacy.assets || []).length;
  const viewCount = (legacy.dashboardViews || []).length;

  const onImport = async () => {
    setBusy(true);
    try {
      await importLegacyState(legacy);
      toast.success('Imported your local data');
      setHidden(true);
    } catch (e) {
      toast.error(`Import failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onDismiss = () => {
    dismissLegacyState();
    setHidden(true);
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
      data-testid="migration-prompt"
    >
      <div className="bg-white border border-border w-full max-w-lg p-7 shadow-[0_12px_40px_rgba(0,0,0,0.18)]">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#F1F6F2] flex items-center justify-center">
              <Database size={18} className="text-[hsl(var(--primary))]" />
            </div>
            <div>
              <div className="font-heading font-bold text-[16px] tracking-tight">Local data found</div>
              <div className="overline mt-1">From your previous session</div>
            </div>
          </div>
          <button onClick={onDismiss} className="text-[#787872] hover:text-[#2B2B2B]" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <p className="text-[13px] text-[#575757] leading-relaxed mb-5">
          We found portfolio data saved in this browser before sign-in. Want to import it into your account?
          This will <strong>replace</strong> the seed data we just created.
        </p>
        <div className="grid grid-cols-2 gap-3 mb-6 text-[12px] font-mono text-[#2B2B2B]">
          <div className="border border-border p-3"><div className="overline mb-1">Sources</div><div className="font-heading font-bold text-base">{sourceCount}</div></div>
          <div className="border border-border p-3"><div className="overline mb-1">Snapshots</div><div className="font-heading font-bold text-base">{snapshotCount}</div></div>
          <div className="border border-border p-3"><div className="overline mb-1">Assets</div><div className="font-heading font-bold text-base">{assetCount}</div></div>
          <div className="border border-border p-3"><div className="overline mb-1">Saved Views</div><div className="font-heading font-bold text-base">{viewCount}</div></div>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onDismiss} className="btn-secondary text-[12px]" data-testid="migration-skip">
            Skip — keep seed data
          </button>
          <button onClick={onImport} disabled={busy} className="btn-primary text-[12px]" data-testid="migration-import">
            {busy ? 'Importing…' : 'Import my data'}
          </button>
        </div>
      </div>
    </div>
  );
}
